import { FIELD_GRID_CLASS, GridCells, Row, SectionCard } from 'components/GridItemList';
import { LockIcon } from 'components/icons';
import { SectionEdit, SectionEditForm, useIsSectionEditing } from 'components/section-edit/SectionEdit';
import { Badge } from 'components/ui/badge';
import { ReactNode } from 'react';
import { get, Path, useFormContext, UseFormReturn } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { InvoiceData, InvoiceForm, InvoiceStatusType } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';

/** An action beside a value of the card, such as "Edit payer billing notes": a link-like button on the value's line. */
export const VALUE_ACTION_CLASS =
    'text-primary hover:text-primary text-meta h-auto rounded-sm p-0 font-medium hover:bg-transparent hover:underline dark:hover:bg-transparent';

/** Why a card the invoice's state keeps from editing reads only. */
export function lockMessageId(status: string): string {
    switch (status) {
        case InvoiceStatusType.POSTPONED:
            return 'invoices.general-tab.lock.postponed';
        case InvoiceStatusType.SENT:
            return 'invoices.general-tab.lock.sent';
        case InvoiceStatusType.PAID:
            return 'invoices.general-tab.lock.paid';
        case InvoiceStatusType.FINALIZED:
            return 'invoices.general-tab.lock.finalised';
        default:
            return 'invoices.general-tab.lock.read-only';
    }
}

/** The card's lock, in the room its Edit would take: why the invoice's state keeps it from editing, unless told. */
export function LockBadge({ invoice, messageId }: { invoice: InvoiceData; messageId?: string }) {
    return (
        <Badge className="bg-neutral-tint text-foreground-secondary">
            <LockIcon data-icon="inline-start" aria-hidden />
            <FormattedMessage id={messageId ?? lockMessageId(invoice.invoiceStatusTypeCode)} />
        </Badge>
    );
}

/** The save on change of a card's fields while they wait for its Save. */
export const NO_SAVE = () => undefined;

const isDirtyEntry = (entry: unknown): boolean =>
    Array.isArray(entry)
        ? entry.some(isDirtyEntry)
        : entry && typeof entry === 'object'
          ? Object.values(entry).some(isDirtyEntry)
          : !!entry;

/** Whether the form holds a change to any of these fields; a list or an object has one when any part of it does. */
export const isAnyFieldDirty = (dirtyFields: object, names: Path<InvoiceForm>[]) =>
    names.some((name) => isDirtyEntry(get(dirtyFields, name)));

/** A group of a card's fields and the save that carries them; it resolves whether the save landed. */
export type FieldGroupSave = [names: Path<InvoiceForm>[], save: () => Promise<boolean>];

/**
 * Runs the saves of the groups with a change, one after another, stopping at the first that does not land. A landed
 * save marks the whole form saved, so the groups after it are put back as changed against their saved values: a
 * retry still sends what has not landed.
 */
export async function saveFieldGroups(
    methods: Pick<UseFormReturn<InvoiceForm>, 'formState' | 'getValues' | 'setValue' | 'reset'>,
    dirtyFields: object,
    groups: FieldGroupSave[]
): Promise<boolean> {
    const runs = groups.filter(([names]) => isAnyFieldDirty(dirtyFields, names));
    const savedValues = methods.formState.defaultValues;
    for (const [index, [, save]] of runs.entries()) {
        if (!(await save())) {
            return false;
        }
        const later = runs.slice(index + 1).flatMap(([names]) => names);
        if (later.length > 0) {
            const laterSaved = Object.fromEntries(later.map((name) => [name, get(savedValues, name)]));
            methods.reset({ ...methods.getValues(), ...laterSaved }, { keepValues: true });
            later.forEach((name) => methods.setValue(name, methods.getValues(name), { shouldDirty: true }));
        }
    }
    return true;
}

/** A yes/no value as the read rows write it. */
export const yesNo = (value: boolean | null | undefined) => t(value ? 'yes' : 'no');

/** A card that reads: its rows on the field grid, then what else it holds. */
export function ReadCard({
    title,
    action,
    rows,
    className,
    children,
}: {
    title: ReactNode;
    action?: ReactNode;
    rows?: Row[];
    className?: string;
    children?: ReactNode;
}) {
    return (
        <SectionCard title={title} action={action} className={className}>
            {rows?.length ? (
                <dl className={cn(FIELD_GRID_CLASS, 'm-0')}>
                    <GridCells rows={rows} />
                </dl>
            ) : null}
            {children}
        </SectionCard>
    );
}

interface InvoiceFormSectionProps {
    /** The card's `?edit=` id. */
    id: string;
    titleId: string;
    /** The title as the read view draws it, where it carries more than its text. */
    title?: ReactNode;
    invoice: InvoiceData;
    /**
     * Whether any of the card's fields edits for this user today, as each field's own lock has it; the others stay
     * read-only in the form. Without one the card shows its lock in Edit's place.
     */
    canEdit: boolean;
    /** The lock's words where the invoice's state is not what keeps the card from editing. */
    lockMessageId?: string;
    rows: Row[];
    /** What the read view draws under its rows. */
    children?: ReactNode;
    /** An action of the read view, before Edit; it goes with Edit while another card edits. */
    headerAction?: ReactNode;
    /** The card's fields of the invoice form, drawn with their save on change turned off. */
    fields: ReactNode;
    /** The invoice form's fields the card edits: Cancel puts each back to its saved value. */
    fieldNames: Path<InvoiceForm>[];
    /**
     * The save path the card's fields take today. A rejection, or `false` for a save that did not land, keeps the card
     * open with its message.
     */
    save: () => boolean | void | Promise<boolean | void>;
    /** What else Cancel puts back, such as a list the card edits in a form of its own. */
    onCancel?: () => void;
}

/**
 * A General card that reads until Edit and then edits in place. Its fields stay fields of the invoice's own form, as
 * in classic, where they save as they change; here they wait for Save, which runs the card's save path once, and
 * Cancel puts them back. Only one card edits at a time, so the form holds no other card's change meanwhile.
 */
export function InvoiceFormSection({
    id,
    titleId,
    title,
    invoice,
    canEdit,
    lockMessageId: lockLabelId,
    rows,
    children,
    headerAction,
    fields,
    fieldNames,
    save,
    onCancel,
}: InvoiceFormSectionProps) {
    const isSectionEditing = useIsSectionEditing();
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            canEdit={canEdit}
            editor={(close) => (
                <InvoiceFormEditor fieldNames={fieldNames} save={save} onCancel={onCancel} close={close}>
                    {fields}
                </InvoiceFormEditor>
            )}
        >
            {(edit) => (
                <ReadCard
                    title={title ?? <FormattedMessage id={titleId} />}
                    rows={rows}
                    action={
                        edit ? (
                            <span className="flex flex-none items-center gap-1">
                                {isSectionEditing ? null : headerAction}
                                {edit}
                            </span>
                        ) : canEdit ? null : (
                            <LockBadge invoice={invoice} messageId={lockLabelId} />
                        )
                    }
                >
                    {children}
                </ReadCard>
            )}
        </SectionEdit>
    );
}

function InvoiceFormEditor({
    fieldNames,
    save,
    onCancel,
    close,
    children,
}: Pick<InvoiceFormSectionProps, 'fieldNames' | 'save' | 'onCancel'> & { close: () => void; children: ReactNode }) {
    const methods = useFormContext<InvoiceForm>();
    // `setValue` back to the saved value, which also clears the field's change, rather than `resetField`, which skips
    // a field no input holds, such as the flag a date's change sets beside it.
    const restoreSaved = <K extends Path<InvoiceForm>>(name: K) =>
        methods.setValue(name, get(methods.formState.defaultValues, name), { shouldDirty: true });
    return (
        <SectionEditForm
            methods={methods}
            formKey="invoice-edit"
            onSave={async () => {
                if ((await save()) === false) {
                    // The page's saves report their failure in a toast and resolve `false`: the card keeps its edit.
                    throw new Error('Save failed');
                }
                close();
            }}
            onCancel={() => {
                fieldNames.forEach((name) => restoreSaved(name));
                methods.clearErrors(fieldNames);
                onCancel?.();
                close();
            }}
        >
            {children}
        </SectionEditForm>
    );
}
